import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './Header.module.scss';
import { authActions } from '../Auth/store';
import { ReactComponent as ZLogoNoText } from '../../assets/svg/logo-noText-black.svg';
import { ReactComponent as ZLogoFull } from '../../assets/svg/zb-logo.svg';
import { ReactComponent as LogoutIcon } from '../../assets/svg/icons/esci.svg';
import { CircleHelp, Menu, X } from 'lucide-react';

import clx from 'classnames';
import useWindowSize from '../../utils/useWindowSize';
import ConfirmModal from '../Common/ConfirmModal';
import { ROUTES } from 'routes/constants';
import { useTranslation } from 'react-i18next';
import PlanButton from '../Scheduler/containers/CalendarHeader/components/PlanButton';
import VerifiedSalonButton from './VerifiedSalonButton';
import SalonSelector from '../SideBar/containers/SalonSelector';
import { toggleDesktopSidebar } from '../SideBar/sidebarEvents';

const Header = ({ setSideBarIsOpen, sideBarIsOpen = false, isCalendar = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams({
    logout: 0,
  });
  const closeLogoutModal = () => setSearchParams({ logout: 0 });

  const toggleSideBar = useCallback(() => {
    if (isMobile) {
      setSideBarIsOpen((isOpen) => !isOpen);
      return;
    }
    toggleDesktopSidebar();
  }, [isMobile, setSideBarIsOpen]);

  const showHeaderSalon = isMobile && !isCalendar;
  const DesktopLogo = isCalendar ? ZLogoNoText : ZLogoFull;
  const supportLabel = t('sideBar.navigation.support');

  return (
    <>
      <ConfirmModal
        isModalOpen={!!+searchParams.get('logout')}
        setIsModalOpen={closeLogoutModal}
        title={t('header.logoutTitle')}
        subtitle={t('header.logoutSubtitle')}
        Icon={LogoutIcon}
        variant="danger"
        cancelLabel={t('common.actions.cancel')}
        confirmLabel={t('common.actions.logout')}
        action={() =>
          dispatch(
            authActions.LOG_OUT.REQUEST({}, () => {
              window.location.href = ROUTES.SIGN_IN;
            }),
          )
        }
      />
      <div
        id="header"
        className={clx(styles.header_wrapper, {
          [styles['header_wrapper_mobile']]: isMobile,
          [styles['header_wrapper_calendar']]: isCalendar,
          [styles['header_wrapper_calendar_mobile']]: isCalendar && isMobile,
          [styles.header_wrapper_with_salon]: showHeaderSalon,
        })}
      >
        <div className={styles.menu_container}>
          {isMobile ? (
            <button
              type="button"
              className={styles.menu_button}
              onClick={toggleSideBar}
              aria-expanded={sideBarIsOpen}
              aria-controls="app-sidebar"
              aria-label={
                sideBarIsOpen ? t('sideBar.closeMenu') : t('sideBar.toggleMenu')
              }
            >
              {sideBarIsOpen ? (
                <X size={24} strokeWidth={2.3} aria-hidden />
              ) : (
                <Menu size={24} strokeWidth={2.3} aria-hidden />
              )}
            </button>
          ) : (
            <DesktopLogo
              className={clx(styles.header_logo, {
                [styles.header_logo_full]: !isCalendar,
                [styles.header_logo_compact]: isCalendar,
              })}
              onClick={toggleSideBar}
              role="button"
              tabIndex={0}
              aria-expanded={sideBarIsOpen}
              aria-label={t('sideBar.toggleMenu')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleSideBar();
                }
              }}
            />
          )}
        </div>
        {showHeaderSalon ? (
          <div className={styles.salon_header_slot}>
            <SalonSelector variant="header" />
          </div>
        ) : null}
        <div className={styles.info_container}>
          {!isCalendar ? (
            <div className={styles.plan_button_container}>
              <VerifiedSalonButton />
              <Link
                to={ROUTES.SUPPORT}
                className={clx(styles.support_button, {
                  [styles.support_button_mobile]: isMobile,
                })}
                aria-label={supportLabel}
                title={supportLabel}
              >
                <CircleHelp size={isMobile ? 20 : 18} strokeWidth={2.3} aria-hidden />
                {!isMobile ? (
                  <span className={styles.support_label}>{supportLabel}</span>
                ) : null}
              </Link>
              <PlanButton showDivider={false} />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Header;
